import React, { useState, useRef, useEffect } from 'react';
import { IoSend, IoAttach, IoCamera, IoVideocam, IoHappyOutline, IoClose, IoDocument } from 'react-icons/io5';
import EmojiPicker from 'emoji-picker-react';

const MAX_ATTACHMENTS = 10;

const MessageInput = ({ onSendMessage, onTyping }) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Allowed file extensions
  const allowedExtensions = {
    image: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    video: ['mp4', 'mkv', 'avi', 'mov', 'wmv'],
    audio: ['mp3', 'wav', 'ogg', 'aac'],
    pdf: ['pdf']
  };

  // Get file type based on extension
  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    for (const [type, extensions] of Object.entries(allowedExtensions)) {
      if (extensions.includes(extension)) {
        return type;
      }
    }
    return null;
  };

  // Validate file
  const validateFile = (file) => {
    const fileType = getFileType(file.name);
    if (!fileType) {
      alert(`File type not supported. Allowed types: ${Object.values(allowedExtensions).flat().join(', ')}`);
      return false;
    }
    
    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 50MB.');
      return false;
    }
    
    return true;
  };

  // Handle file selection (accepts one file or an array/FileList of files)
  const handleFileSelect = (files) => {
    const fileList = Array.isArray(files) ? files : (files?.length !== undefined ? Array.from(files) : [files]);

    setAttachedFiles(prev => {
      const remainingSlots = MAX_ATTACHMENTS - prev.length;
      if (remainingSlots <= 0) {
        alert(`You can attach up to ${MAX_ATTACHMENTS} files at once.`);
        return prev;
      }
      if (fileList.length > remainingSlots) {
        alert(`Only ${remainingSlots} more file(s) can be added (max ${MAX_ATTACHMENTS}).`);
      }

      const accepted = fileList.slice(0, remainingSlots).filter(validateFile);
      const newEntries = accepted.map(file => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name,
        size: file.size,
        type: getFileType(file.name),
        preview: null
      }));

      // Create previews for images
      newEntries.forEach(entry => {
        if (entry.type === 'image') {
          const reader = new FileReader();
          reader.onload = (e) => {
            setAttachedFiles(current => current.map(f =>
              f.id === entry.id ? { ...f, preview: e.target.result } : f
            ));
          };
          reader.readAsDataURL(entry.file);
        }
      });

      return [...prev, ...newEntries];
    });
  };

  // Remove an attached file by id
  const removeFile = (id) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() || attachedFiles.length > 0) {
      onSendMessage(message.trim(), attachedFiles);
      setMessage('');
      setAttachedFiles([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  // Listen for file drop events from chat window
  useEffect(() => {
    const handleFileDropped = (event) => {
      if (event.files && event.files.length) {
        handleFileSelect(event.files);
      } else if (event.file) {
        handleFileSelect([event.file]);
      }
    };

    window.addEventListener('fileDropped', handleFileDropped);
    return () => window.removeEventListener('fileDropped', handleFileDropped);
  }, []);

  const handleEmojiClick = (emojiData) => {
    setMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };



  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="message-input-container">
      {/* WhatsApp-like File Preview */}
      {attachedFiles.length === 1 && (
        <div className="file-preview-container">
          <div className="file-preview-content">
            {attachedFiles[0].type === 'image' && attachedFiles[0].preview ? (
              <div className="image-preview">
                <img src={attachedFiles[0].preview} alt={attachedFiles[0].name} className="preview-image" />
                <div className="image-overlay">
                  <div className="file-info">
                    <span className="file-name">{attachedFiles[0].name}</span>
                    <span className="file-size">{formatFileSize(attachedFiles[0].size)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="document-preview">
                <div className="document-icon">
                  {attachedFiles[0].type === 'video' && <IoVideocam size={48} />}
                  {attachedFiles[0].type === 'audio' && <span style={{fontSize: '48px'}}>🎵</span>}
                  {attachedFiles[0].type === 'image' && <IoCamera size={48} />}
                  {attachedFiles[0].type === 'pdf' && <IoDocument size={48} />}
                </div>
                <div className="document-info">
                  <div className="file-name">{attachedFiles[0].name}</div>
                  <div className="file-size">{formatFileSize(attachedFiles[0].size)}</div>
                </div>
              </div>
            )}
            <button
              type="button"
              className="remove-preview-btn"
              onClick={() => removeFile(attachedFiles[0].id)}
              title="Remove file"
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Multiple files preview grid */}
      {attachedFiles.length > 1 && (
        <div className="file-preview-container">
          <div className="file-preview-grid">
            {attachedFiles.map(f => (
              <div key={f.id} className="file-preview-thumb">
                {f.type === 'image' && f.preview ? (
                  <img src={f.preview} alt={f.name} />
                ) : (
                  <div className="file-preview-thumb-icon">
                    {f.type === 'video' && <IoVideocam size={22} />}
                    {f.type === 'audio' && <span style={{fontSize: '22px'}}>🎵</span>}
                    {f.type === 'image' && <IoCamera size={22} />}
                    {f.type === 'pdf' && <IoDocument size={22} />}
                  </div>
                )}
                <button
                  type="button"
                  className="remove-thumb-btn"
                  onClick={() => removeFile(f.id)}
                  title="Remove file"
                >
                  <IoClose size={14} />
                </button>
              </div>
            ))}
            <span className="file-preview-count">{attachedFiles.length}/{MAX_ATTACHMENTS}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="message-form">
        <div className="input-wrapper">
          <button
            type="button"
            className="action-btn attach-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach files"
          >
            <IoAttach size={18} />
          </button>
          <button 
            type="button" 
            className="emoji-btn"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            title="Add emoji"
          >
            <IoHappyOutline size={20} />
          </button>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => { setMessage(e.target.value); onTyping && onTyping(); }}
            onKeyPress={handleKeyPress}
            placeholder={attachedFiles.length > 0 ? "Add a caption..." : "Type a message..."}
            className="message-textarea"
            rows="1"
          />
          <button
            type="submit"
            className={`send-button ${(message.trim() || attachedFiles.length > 0) ? 'active' : ''}`}
            disabled={!(message.trim() || attachedFiles.length > 0)}
          >
            <IoSend size={18} />
          </button>
        </div>

        {showEmojiPicker && (
          <div className="emoji-picker-containers">
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              width={320}
              height={400}
              theme="light"
              searchDisabled={false}
              skinTonesDisabled={false}
              categories={[
                { category: 'suggested', name: 'Recently Used' },
                { category: 'smileys_people', name: 'Smileys & People' },
                { category: 'animals_nature', name: 'Animals & Nature' },
                { category: 'food_drink', name: 'Food & Drink' },
                { category: 'travel_places', name: 'Travel & Places' },
                { category: 'activities', name: 'Activities' },
                { category: 'objects', name: 'Objects' },
                { category: 'symbols', name: 'Symbols' },
                { category: 'flags', name: 'Flags' }
              ]}
              previewConfig={{
                showPreview: false
              }}
            />
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mkv,.avi,.mov,.wmv,.mp3,.wav,.ogg,.aac,.pdf"
          onChange={(e) => {
            if (e.target.files.length) handleFileSelect(e.target.files);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
      </form>
    </div>
  );
};

export default MessageInput;